import React, { useEffect, useRef, useState } from 'react'
import { effectiveWindow, staleRangeLabel } from './tracpatch.window'
import { connect } from 'react-redux'
import { Row, Button } from 'antd'
import * as d3 from 'd3'
import moment from 'moment'
import { pickRomTestPerDay } from './tracpatch.rom.filter'
import { useRomGoalTarget } from './tracpatch.rom.target'
import '../../../css/patient.scss'
import { CHART_COLORS } from '../../../util/chartPalette'
import { DataViewToggle, ChartDataGrid } from '../../graph/chartDataView'
import { styleAxisTicks, AXIS_TITLE_FONT_SIZE, AXIS_TITLE_FONT_WEIGHT, ShowValuesSwitch } from './tracpatch.chart.style'

const TracPatchFlexionChart = (props) => {
  const svgRef = useRef(null)
  const containerRef = useRef(null)
  const tooltipRef = useRef(null)
  const [days, setDays] = useState(30)
  const [view, setView] = useState('chart')
  // Off by default, as on the Steps chart: a number at every point turns a
  // 30-day series into a wall of digits. See tracpatch.chart.style.js.
  const [showValues, setShowValues] = useState(false)

  // Configured Progress-Test goal for the patient's current post-op week
  // (per-patient override → clinic setting → default). Drives the green line.
  const goalTarget = useRomGoalTarget(props.patient)

  useEffect(() => {
    if (view === 'chart') draw()
  }, [props.data, days, view, goalTarget.signature, showValues]) // eslint-disable-line react-hooks/exhaustive-deps

  var getDailyFlexion = () => {
    if (!props.data || props.data.length === 0) return { dailyData: [] }

    // Falls back to the patient's latest ROM TEST when the requested range
    // holds none — see tracpatch.window.js.
    //
    // The extractor ignores non-test records on purpose: pickRomTestPerDay
    // below keeps only `is_rom_test` rows, so anchoring the window to a
    // passive ROM reading would move the window to a day the chart then
    // renders empty anyway — a stale banner over a blank chart.
    const win = effectiveWindow(
      props.data,
      days,
      (r) => (r && r.is_rom_test ? r.startTime : null)
    )
    const defaultStart = win.windowStart
    const today = win.windowEnd

    // Only active ROM Tests with a meaningful range; latest endTime per
    // day wins. Days with no qualifying ROM Test render as a gap.
    const byDay = pickRomTestPerDay(props.data, { windowStart: defaultStart })

    const dataDays = Object.keys(byDay).sort()
    if (dataDays.length === 0) return { dailyData: [] }

    const firstData = moment(dataDays[0], 'YYYY-MM-DD').startOf('day')
    const lastData = moment(dataDays[dataDays.length - 1], 'YYYY-MM-DD').startOf('day')
    const windowStart = firstData.isAfter(defaultStart) ? firstData : defaultStart
    // Right-justify: end at the last day with data (no blank tail past today)
    const windowEnd = lastData.isBefore(today) ? lastData : today

    const dailyData = []
    const cursor = windowStart.clone()
    while (cursor.isSameOrBefore(windowEnd)) {
      const key = cursor.format('YYYY-MM-DD')
      const rec = byDay[key]
      dailyData.push({
        date: cursor.clone().toDate(),
        // Actual timestamp of the day's reading (latest ROM test that day)
        // for the data-grid's date+time column.
        time: rec ? rec.endTime || rec.startTime : null,
        flexion: rec && rec.flexion != null ? rec.flexion : null,
      })
      cursor.add(1, 'day')
    }
    return { dailyData, windowStart: windowStart.toDate(), windowEnd: windowEnd.toDate(), isStale: win.isStale }
  }

  var draw = () => {
    const { dailyData, windowStart, windowEnd } = getDailyFlexion()
    if (!dailyData || dailyData.length === 0) return

    const margin = { top: 20, right: 20, bottom: 60, left: 50 }
    const screenWidth = containerRef.current ? containerRef.current.offsetWidth : 400
    const screenHeight = 300
    const width = screenWidth - margin.left - margin.right
    const height = screenHeight - margin.top - margin.bottom

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()

    const g = svg
      .attr('width', screenWidth)
      .attr('height', screenHeight)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)

    const x = d3.scaleTime().domain([windowStart, windowEnd]).range([0, width])

    // Fixed y-axis so charts are visually comparable across patients and over time.
    // 170 cap leaves headroom above typical 130° flexion for hyperflexion readings.
    const y = d3.scaleLinear().domain([0, 170]).range([height, 0])

    const tickCount = days > 20 ? 8 : 7
    g.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(x).ticks(tickCount).tickFormat(d3.timeFormat('%m/%d')))
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('dx', '-.8em')
      .attr('dy', '.15em')
      .attr('transform', 'rotate(-45)')
      .call(styleAxisTicks, days > 20 ? 10 : 11)
      .style('fill', 'black')
      .style('stroke', 'none')

    g.append('text')
      .attr('text-anchor', 'end')
      .attr('x', width / 2 + margin.left)
      .attr('y', height + margin.bottom - 5)
      .attr('stroke', 'none')
      .attr('fill', 'black')
      .style('font-size', AXIS_TITLE_FONT_SIZE)
      .style('font-weight', AXIS_TITLE_FONT_WEIGHT)
      .text('Date')

    g.append('g')
      .call(d3.axisLeft(y).ticks(6))
      .selectAll('text')
      .style('fill', 'black')
      .style('stroke', 'none')
      .call(styleAxisTicks, 10)

    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', -margin.left + 12)
      .attr('x', -height / 2)
      .attr('text-anchor', 'middle')
      .attr('fill', 'black')
      .attr('stroke', 'none')
      .style('font-size', AXIS_TITLE_FONT_SIZE)
      .style('font-weight', AXIS_TITLE_FONT_WEIGHT)
      .text('Degrees (°)')

    const surgeryRaw = props.patient?.surgery?.date
    if (surgeryRaw) {
      const surgeryDate = moment(surgeryRaw).startOf('day').toDate()
      if (surgeryDate >= windowStart && surgeryDate <= windowEnd) {
        const surgeryX = x(surgeryDate)
        g.append('line')
          .attr('x1', surgeryX)
          .attr('x2', surgeryX)
          .attr('y1', 0)
          .attr('y2', height)
          .attr('stroke', '#595959')
          .attr('stroke-width', 1.5)
          .attr('stroke-dasharray', '4,3')
        const labelText = `Surgery ${moment(surgeryDate).format('MM/DD')}`
        const labelOnLeft = surgeryX > width - 80
        g.append('text')
          .attr('x', surgeryX + (labelOnLeft ? -4 : 4))
          .attr('y', 10)
          .attr('text-anchor', labelOnLeft ? 'end' : 'start')
          .attr('fill', '#595959')
          .attr('stroke', 'none')
          .style('font-size', '11px')
          .style('font-weight', '600')
          .text(labelText)
      }
    }

    // Green target line — the configured Progress-Test flexion goal stepped by
    // each date's post-op week (so it changes across the window instead of
    // sitting flat at today's goal). Drawn before the data line so the blue
    // readings sit on top.
    if (goalTarget.surgeryDate) {
      const targetSeries = []
      const cursor = moment(windowStart)
      const end = moment(windowEnd)
      while (cursor.isSameOrBefore(end)) {
        const v = goalTarget.goalForDate(cursor.toDate(), 'flexion')
        if (v != null && v <= 170) {
          targetSeries.push({ date: cursor.clone().toDate(), value: v })
        }
        cursor.add(1, 'day')
      }
      if (targetSeries.length > 0) {
        const targetLine = d3
          .line()
          .x((d) => x(d.date))
          .y((d) => y(d.value))
          .curve(d3.curveStepAfter)
        g.append('path')
          .datum(targetSeries)
          .attr('fill', 'none')
          .attr('stroke', CHART_COLORS.completeGreen)
          .attr('stroke-width', 2)
          .attr('stroke-dasharray', '6,4')
          .attr('d', targetLine)
        const last = targetSeries[targetSeries.length - 1]
        g.append('text')
          .attr('x', x(last.date))
          .attr('y', y(last.value) - 4)
          .attr('text-anchor', 'end')
          .attr('fill', CHART_COLORS.completeGreen)
          .attr('stroke', 'none')
          .style('font-size', '11px')
          .style('font-weight', '600')
          .text(`Target ${Math.round(last.value)}°`)
      }
    }

    const tooltip = d3.select(tooltipRef.current)

    const color = CHART_COLORS.lineBlue
    const lineGen = d3
      .line()
      .x((d) => x(d.date))
      .y((d) => y(d.flexion))

    // Filter to defined points so the line connects across days without readings.
    const linePoints = dailyData.filter((d) => d.flexion != null)
    g.append('path')
      .datum(linePoints)
      .attr('fill', 'none')
      .attr('stroke', color)
      .attr('stroke-width', 2)
      .attr('d', lineGen)

    g.selectAll('.dot-flexion')
      .data(dailyData.filter((d) => d.flexion != null))
      .enter()
      .append('circle')
      .attr('class', 'dot-flexion')
      .attr('cx', (d) => x(d.date))
      .attr('cy', (d) => y(d.flexion))
      .attr('r', 4)
      .attr('fill', color)
      .style('cursor', 'pointer')
      .on('mouseover', function (d) {
        tooltip
          .style('visibility', 'visible')
          .html(`<b>Date</b>: ${moment(d.date).format('MM/DD')}<br/><b>Flexion</b>: ${Math.round(d.flexion)}°`)
          .style('top', d3.select(this).attr('cy') - 50 + 'px')
          .style('left', parseInt(d3.select(this).attr('cx')) + margin.left + 10 + 'px')
      })
      .on('mouseout', () => tooltip.style('visibility', 'hidden'))

    // Value labels -- OFF unless the chart's "Show values" switch is on.
    if (!showValues) return
    const labelData = dailyData.filter((d) => d.flexion != null)
    // Throttle labels only when there are too many points to fit cleanly.
    const labelStep = labelData.length > 10 ? 2 : 1
    labelData.forEach((d, i) => {
      const fromEnd = labelData.length - 1 - i
      if (fromEnd % labelStep !== 0) return
      g.append('text')
        .attr('class', 'point-label')
        .attr('x', x(d.date))
        .attr('y', y(d.flexion) - 3)
        .attr('text-anchor', 'middle')
        .attr('fill', color)
        .style('font-size', '10px')
        .style('font-weight', '600')
        .style('pointer-events', 'none')
        .text(Math.round(d.flexion) + '°')
    })

  }

  const { dailyData } = getDailyFlexion()
  const stale = effectiveWindow(props.data, days, (r) => (r && r.is_rom_test ? r.startTime : null))
  const gridRows = dailyData
    .filter((d) => d.flexion != null)
    .map((d) => ({ date: d.time || d.date, value: Math.round(d.flexion) + '°' }))

  return (
    <>
      <center>
        <h4>TracPatch Flexion</h4>
      </center>
      {stale.isStale && (
        <Row justify='start' style={{ marginBottom: 8, paddingLeft: 12 }}>
          <div style={{ background:'#fff8e1', border:'1px solid var(--orange-color, #f5881f)',
                        borderRadius:8, padding:'6px 12px', fontSize:12, color:'#8a4b00', maxWidth:'100%' }}>
            {staleRangeLabel(stale.windowStart, stale.windowEnd, days)}
          </div>
        </Row>
      )}
      <Row justify='start' className='chartControls' style={{ marginBottom: 8, paddingLeft: 12 }}>
        <Button.Group size='small'>
          <Button type={days === 7 ? 'primary' : 'default'} onClick={() => setDays(7)}>7 Days</Button>
          <Button type={days === 14 ? 'primary' : 'default'} onClick={() => setDays(14)}>14 Days</Button>
          <Button type={days === 30 ? 'primary' : 'default'} onClick={() => setDays(30)}>30 Days</Button>
        </Button.Group>
        <ShowValuesSwitch id='flexion-show-values' checked={showValues} onChange={setShowValues} />
        <DataViewToggle
          active={view === 'data'}
          onClick={() => setView(view === 'data' ? 'chart' : 'data')}
        />
      </Row>
      {dailyData.length > 0 ? (
        view === 'data' ? (
          <ChartDataGrid rows={gridRows} valueLabel='Flexion (°)' csvName='tracpatch-flexion' />
        ) : (
          <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
            <div ref={tooltipRef} className='chartToolTip' style={{ position: 'absolute', visibility: 'hidden', zIndex: 10 }} />
            <svg style={{ width: '100%', height: '300px' }} ref={svgRef} />
          </div>
        )
      ) : (
        <Row justify='center' align='middle' style={{ height: 200 }}>
          <h2>Data not available</h2>
        </Row>
      )}
    </>
  )
}

const mapStateToProps = (state) => ({ patient: state.patient.patient })
const mapDispatchToProps = () => ({})
export default connect(mapStateToProps, mapDispatchToProps)(TracPatchFlexionChart)
